import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { Button } from "../../../components/ui/button";
import { cn } from "../../../handlers/cn.utils";
import {
	chooseSubscription,
	isSubscriptionActive,
	nextTierProgress,
	type PointsSummary,
	type Subscription,
	type TierName,
	toTierName,
} from "../../../handlers/rewards";
import { currencyFormat } from "../../../handlers/utilityFunctions";

interface PlanPanelProps {
	payments: Subscription[];
	summary: PointsSummary;
	isLoading: boolean;
}

/** Inside this window the plan is close enough to lapsing that renewing is the useful action. */
const EXPIRING_SOON_DAYS = 14;

/** The belt. Each tier gets its own colour so the card is identifiable before it is read. */
const TIER_BELT: Record<TierName, string> = {
	BRONZE: "bg-amber-600",
	SILVER: "bg-slate-400",
	GOLD: "bg-yellow-500",
	PLATINUM: "bg-indigo-500",
};

/**
 * What the account is on today, and the one thing worth doing about it.
 *
 * Subscription state used to live only in Settings, two navigation levels from anywhere a reader
 * would think to look. Putting it beside the balance is the point of the redesign: the points and
 * the thing they buy are one subject.
 */
function PlanPanel({ payments, summary, isLoading }: PlanPanelProps) {
	const subscription = chooseSubscription(payments);
	const active = isSubscriptionActive(subscription);

	if (isLoading) {
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-6">
				<div className="h-4 w-24 rounded bg-gray-100" />
				<div className="mt-4 h-7 w-32 rounded bg-gray-100" />
			</div>
		);
	}

	const tier = active ? toTierName(subscription?.package) : "BRONZE";
	const daysLeft = active && subscription?.expireAt ? dayjs(subscription.expireAt).diff(dayjs(), "day") : 0;
	const expiringSoon = active && daysLeft <= EXPIRING_SOON_DAYS;
	const nextTier = nextTierProgress(tier, summary.available);

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-6">
			<div className="flex items-center gap-2.5">
				<span className={cn("h-5 w-1.5 shrink-0 rounded-full", TIER_BELT[tier])} aria-hidden="true" />
				<p className="text-xl font-bold capitalize text-gray-900 font-plus-jakarta">{tier.toLowerCase()}</p>
				<span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
					Current plan
				</span>
			</div>

			{active && subscription?.expireAt && (
				/* Fixed terms rather than auto-renewing, so this is the date access stops unless it is
				   paid again — not a date money moves. */
				<p className={cn("mt-2 text-sm font-inter", expiringSoon ? "text-amber-700" : "text-gray-600")}>
					Runs until <strong className="font-medium">{dayjs(subscription.expireAt).format("D MMM YYYY")}</strong>
					{daysLeft > 0 && ` · ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
				</p>
			)}

			{/* Progress toward the biggest discount the next tier accepts — not toward its price. Points
			    offset a plan 1 TZS each up to a cap and never buy one outright, so a bar drawn against
			    the price could never fill and would promise a free upgrade. */}
			{nextTier && (
				<div className="mt-5">
					<div className="flex items-baseline justify-between gap-2">
						<p className="text-xs font-medium text-gray-900 font-inter capitalize">
							Next: {nextTier.tier.toLowerCase()}
						</p>
						<p className="text-xs text-gray-500 tabular-nums font-inter">
							{currencyFormat(nextTier.earned)} / {currencyFormat(nextTier.maxDiscountPoints)} pts
						</p>
					</div>
					<div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
						<div
							className="h-full rounded-full bg-primary-700 origin-left transition-transform duration-700 ease-out motion-reduce:transition-none"
							style={{ transform: `scaleX(${nextTier.pct / 100})` }}
							role="progressbar"
							aria-valuenow={nextTier.earned}
							aria-valuemin={0}
							aria-valuemax={nextTier.maxDiscountPoints}
							aria-label={`Points toward the ${nextTier.tier.toLowerCase()} discount`}
						/>
					</div>
					<p className="mt-1.5 text-xs text-gray-500 font-inter leading-relaxed">
						{nextTier.remaining > 0 ? (
							<>
								{currencyFormat(nextTier.remaining)} more points to take Tsh{" "}
								{currencyFormat(nextTier.maxDiscountPoints)} off Tsh {currencyFormat(nextTier.price)}.
							</>
						) : (
							<>
								Your points take the full Tsh {currencyFormat(nextTier.maxDiscountPoints)} off Tsh{" "}
								{currencyFormat(nextTier.price)}.
							</>
						)}
					</p>
				</div>
			)}

			{/* Upgrading is optional, so it does not compete with the points panel's ask — earning is
			    what this page is for. Only a plan about to lapse gets the primary treatment: that is a
			    deadline on access already paid for, not an upsell. */}
			<Button variant={active && expiringSoon ? "default" : "outlineSecondary"} className="mt-5 w-full" asChild>
				<Link to={active && !expiringSoon ? "/subscription" : "/payments"}>
					{!active ? "Upgrade" : expiringSoon ? "Renew now" : "Manage plan"}
				</Link>
			</Button>
		</div>
	);
}

export default PlanPanel;
